/**
 * The hooks components read the kiosk with. Each one subscribes to one slice, so a component
 * re-renders only when that slice changes.
 */
import { stageOf, type Stage } from '../stage/catalog';
import { useMemo } from 'react';
import { resolveAppearance, type Appearance } from '../core/appearance';
import { detailClosesAt, type OpenDetail } from '../core/detail';
import { themeById, type Theme } from '../themes/themes';
import type { Connection } from '../core/kiosk';
import type { Conversation } from '../core/conversation';
import { shallowEqual } from '../core/store';
import type { UiState } from '../core/ui-state';
import type { Session } from '../core/wire';
import { createTranslator, type Translate } from '../i18n/translator';
import type { ScreenContext, ScreenDef } from '../packs/contract';
import { activeMode, activeModeId, layoutFor, screenContext, screenOf } from '../packs/screen';
import type { ModeDefinition } from '../modes/modes';
import { useApp } from './app';
import { useStore } from './useStore';

export { shallowEqual };

export function useSession<T>(select: (s: Session | null) => T, isEqual?: (a: T, b: T) => boolean): T {
  return useStore(useApp().kiosk.stores.session, select, isEqual);
}

export function useConversation<T>(select: (c: Conversation) => T, isEqual?: (a: T, b: T) => boolean): T {
  return useStore(useApp().kiosk.stores.conversation, select, isEqual);
}

export function useUi<T>(select: (u: UiState) => T, isEqual?: (a: T, b: T) => boolean): T {
  return useStore(useApp().ui, select, isEqual);
}

export function useConnection(): Connection {
  return useStore(useApp().kiosk.stores.connection, (c) => c);
}

export function useLanguage(): string {
  return useSession((s) => s?.language ?? 'it');
}

export function useTranslate(): Translate {
  const { catalogs } = useApp();
  const language = useLanguage();
  return useMemo(() => createTranslator(catalogs, language), [catalogs, language]);
}

export interface CurrentScreen {
  name: string | null;
  def: ScreenDef | null;
  known: boolean;
  ctx: ScreenContext | null;
}

export function useScreen(): CurrentScreen {
  const { pack } = useApp();
  const session = useSession((s) => s);
  const draft = useUi((u) => u.draft);
  const t = useTranslate();
  return useMemo(() => {
    if (!session) return { name: null, def: null, known: false, ctx: null };
    const { def, known } = screenOf(pack, session.state);
    return { name: session.state, def, known, ctx: screenContext(session, t, draft) };
  }, [pack, session, draft, t]);
}

export function useMode(): ModeDefinition {
  const app = useApp();
  const fromSession = useSession((s) => s?.accessibility.mode ?? null);
  const override = useUi((u) => u.modeOverride);
  const announced = useStore(app.kiosk.stores.announcedMode, (m) => m);
  return activeMode(activeModeId(fromSession, override, announced));
}

/** The theme in force: chosen in Settings, else the one the page started with (?theme=). */
export function useTheme(): Theme {
  const { flags } = useApp();
  const chosen = useUi((u) => u.settings.theme);
  return themeById(chosen ?? flags.theme);
}

/** The look in force: defaults, then the theme's, then the preset's, then what Settings changed. */
export function useAppearance(): Appearance {
  const app = useApp();
  const theme = useTheme();
  const presetId = useUi((u) => u.presetId);
  const draftPreset = useUi((u) => u.draftPreset);
  const settings = useUi((u) => u.settings);
  return useMemo(() => {
    const { theme: _theme, preset: _preset, ...look } = settings;
    return resolveAppearance(theme.appearance, (draftPreset ?? app.presets.get(presetId))?.appearance, look);
  }, [app, theme, presetId, draftPreset, settings]);
}

/** The stage shown: the avatar and the background (stage/catalog.ts), from Settings, the pack and the catalog. */
export function useStage(): Stage {
  const { stage, pack } = useApp();
  const { background } = useAppearance();
  return useMemo(() => stageOf(stage, pack.stage, background), [stage, pack.stage, background]);
}

export function useResolvedLayout() {
  const app = useApp();
  const presetId = useUi((u) => u.presetId);
  const draftPreset = useUi((u) => u.draftPreset);
  const state = useSession((s) => s?.state ?? null);
  const mode = useMode();
  const { face } = useAppearance();
  return useMemo(() => {
    const def = state ? screenOf(app.pack, state).def : null;
    return layoutFor({
      presets: app.presets,
      presetId,
      draftPreset,
      pack: app.pack,
      screen: state && def ? { name: state, def } : null,
      mode,
      knownModules: new Set(app.modules.keys()),
      face,
    });
  }, [app, presetId, draftPreset, state, mode, face]);
}

/** Dev details on screen: ?dev=1, the dev panel open, or "turn details" on in Settings. */
export function useDevMode(): boolean {
  const { flags } = useApp();
  const panelOpen = useUi((u) => u.panel === 'dev');
  const { turnDetails } = useAppearance();
  return flags.dev || panelOpen || turnDetails;
}

/**
 * The detail a reply opened on this item's card (core/detail.ts): whether it is open and when it closes
 * by itself (Settings' `detailSeconds`), and the hands to close it or keep it open (a touch on it).
 */
export function useDetail(item: string): {
  detail: OpenDetail | null;
  closesAt: number | null;
  close: () => void;
  touched: () => void;
} {
  const { ui } = useApp();
  const detail = useUi((u) => (u.detail?.item === item ? u.detail : null));
  const { detailSeconds } = useAppearance();
  return {
    detail,
    closesAt: detail ? detailClosesAt(detail, detailSeconds) : null,
    close: () => ui.set((s) => (s.detail?.item === item ? { ...s, detail: null } : s)),
    touched: () => ui.set((s) => (s.detail?.item === item ? { ...s, detail: { ...s.detail, since: Date.now() } } : s)),
  };
}
